// @flow
import axios from "axios";
import TVGConf from "@tvg/conf";
import { replaceCAPIVariables } from "@tvg/utils/capiUtils";
import { formatDateToMMDDYYYYhhmm } from "@tvg/formatter/dates";

import type {
  ResponseObject,
  Payload,
  LoginServerResponse,
  ServerError
} from "./Types.js.flow";
import geoComplyMiddleware from "./middleware/geocomply";

export const errors = {
  ACCOUNT_DISABLED:
    'Your account has been disabled. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  ACCOUNT_LOCKED:
    "Your account has been locked. Please use the forgot credentials link or call 1-888-752-9884 for assistance.",
  ACCOUNT_UPDATE_REQUIRED:
    'Your account requires an update. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  BLOCKED_SERVICE:
    'We\'re unable to confirm your location at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a>. (Error 4)', // SE-1735 (error message of fail login)
  BLOCKED_SOFTWARE:
    'We\'re unable to confirm your location at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a>. (Error 4)',
  GEO_SERVICE_FAILURE:
    'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  INVALID_CREDENTIALS:
    'Please check your login information and try again. If you are unable to log in, try <a href="/forgot-credentials" target="_self">resetting your credentials</a>.',
  INVALID_GEO:
    'We\'re unable to confirm you are located within New Jersey. If you have moved, please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> to update your address. (Error 2)',
  INVALID_GEOPACKET:
    'We\'re unable to confirm you are located within New Jersey. If you have moved, please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> to update your address. (Error 2)',
  INVALID_WAGERING_STATE:
    'It seems you are trying to access the application from a non legal state. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  LOGIN_FAILED:
    'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  LOGIN_REDIRECT:
    "Sorry you are on the wrong application. You will be redirected to the proper application.",
  MISSING_TOKEN:
    'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  NON_LEGAL_STATE:
    'It seems you are trying to access the application from a non legal state. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  OUT_OF_BOUNDARY:
    'We\'re unable to confirm you are located within New Jersey. If you have moved, please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> to update your address. (Error 2)',
  SESSION_NOT_FOUND:
    'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  SESSION_TIMEOUT:
    'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  UNCONFIRMED_BOUNDARY:
    'We\'re unable to confirm you are located within New Jersey. If you have moved, please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> to update your address. (Error 2)',
  USER_SESSION_LIMIT_REACHED:
    'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.',
  ACCOUNT_IN_TIMEOUT:
    'A time out period is active on your account.<br />Please <a target="_blank" href="/redirectengine?type=messageus">contact our customer service</a> for assistance.',
  ACCOUNT_SUSPENDED:
    'Your account is currently ~motive~<br />Please <a target="_blank" href="/redirectengine?type=messageus">contact our customer service</a> for assistance.',
  ACCOUNT_SELF_EXCLUDED:
    'Your account is currently ~motive~<br />Please <a target="_blank" href="/redirectengine?type=messageus">contact our customer service</a> for assistance.',
  InvalidPinException:
    'Please check your login information and try again. If you are unable to log in, try <a href="/forgot-credentials" target="_self">resetting your credentials</a>.'
};

export const errorMapping = {
  ACCOUNT_DISABLED: "loginFailureAccountDisabled",
  ACCOUNT_LOCKED: "loginFailureAccountLocked",
  ACCOUNT_UPDATE_REQUIRED: "loginFailureAccountUpdateRequired",
  BLACK_LISTED_LOCATION: "blackListedLocation",
  BLOCKED_SERVICE: "geoLocationBlockedService",
  BLOCKED_SOFTWARE: "geoLocationBlockedSoftware",
  GEO_SERVICE_FAILURE: "loginFailureGeneric",
  INVALID_CREDENTIALS: "loginFailureInvalidCredentials",
  INVALID_GEO: "geoLocationUnconfirmedOrInvalid",
  INVALID_GEOPACKET: "geoLocationUnconfirmedOrInvalid",
  INVALID_WAGERING_STATE: "loginFailureInvalidWageringState",
  LOGIN_FAILED: "loginFailureGeneric",
  LOGIN_REDIRECT: "loginSiteRedirect",
  MISSING_TOKEN: "apiMissingUserSessionToken",
  NON_LEGAL_STATE: "loginFailureInvalidWageringState",
  OUT_OF_BOUNDARY: "geoLocationOutOfBoundary",
  SESSION_NOT_FOUND: "apiUserSessionNotFound",
  SESSION_TIMEOUT: "apiUserSessionTimedOut",
  UNCONFIRMED_BOUNDARY: "geoLocationUnconfirmedOrInvalid",
  USER_SESSION_LIMIT_REACHED: "loginFailureUserSessionLimitReached",
  ACCOUNT_IN_TIMEOUT: "loginFailureRGTimeout",
  ACCOUNT_SUSPENDED: "loginFailureRGSuspendedOrClose",
  ACCOUNT_SELF_EXCLUDED: "loginFailureRGSuspendedOrClose",
  InvalidPinException: "invalidPinException"
};

export const errorTitleMapping = {
  ACCOUNT_DISABLED: "loginFailureTitleAccountDisabled",
  ACCOUNT_LOCKED: "loginFailureTitleAccountLocked",
  INVALID_CREDENTIALS: "loginFailureTitleUnrecognizedCredentials",
  DEFAULT_MESSAGE: "loginFailureTitleDefault",
  InvalidPinException: "loginErrorInvalidPin"
};

export const tvgHeaders = (): Object => ({
  "content-type": "application/json",
  "x-tvg-context": TVGConf().context(),
  "x-requested-with": "XMLHttpRequest" // we need this some the request works on old request
});

export const onLoginSuccess = (response: {
  data: LoginServerResponse
}): ResponseObject => ({
  success: {
    status: "success",
    data: response.data
  },
  error: null
});

export const onLoginError = (
  error: ServerError,
  messages: {}
): ResponseObject => {
  if (!error.response || !error.response.data || error.response.status >= 500) {
    return {
      error: {
        status: "error",
        message:
          'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.'
      },
      success: null
    };
  }

  const redirect = error.response.data.redirectUrl
    ? { redirectUrl: error.response.data.redirectUrl }
    : {};

  const cmsLabel = errorMapping[error.response.data.exception];
  const cmsTitle =
    errorTitleMapping[error.response.data.exception] ||
    errorTitleMapping.DEFAULT_MESSAGE;
  const fallbackErrorMsg =
    'An error occurred and we could not log you in at this time. Please <a target="_blank" href="redirectengine?type=messageus" style="color:inherit; text-decoration: underline; font-weight:bold;">message our customer service</a> for assistance.';
  let message =
    messages && cmsLabel && messages[cmsLabel]
      ? messages[cmsLabel]
      : errors[error.response.data.exception] || fallbackErrorMsg;

  const title = messages
    ? messages[cmsTitle]
    : errors[error.response.data.exception];

  if (cmsLabel === errorMapping.ACCOUNT_IN_TIMEOUT) {
    const endDate = error.response.data.responsibleGamingExclusion.endDate;
    message = replaceCAPIVariables(message, {
      exclusionTime: formatDateToMMDDYYYYhhmm(endDate)
    });
  } else if (
    cmsLabel === errorMapping.ACCOUNT_SUSPENDED ||
    cmsLabel === errorMapping.ACCOUNT_SELF_EXCLUDED
  ) {
    const motive =
      error.response.data.responsibleGamingExclusion.type === "SUSPEND"
        ? "suspended"
        : "disabled";
    message = replaceCAPIVariables(message, {
      motive
    });
  }

  return {
    error: {
      status: "error",
      ...{
        title,
        message
      },
      ...redirect
    },
    success: null
  };
};

// $FlowFixMe: suppressing this error until we can refactor
const loginRequest = (payload: Payload, geo: {}): Promise<ResponseObject> =>
  axios({
    method: "post",
    url: `${TVGConf().config().service.usa}/login`,
    data: {
      account: payload.account,
      pin: payload.pin,
      stateAbbr: payload.stateAbbr,
      ...geo
    },
    headers: tvgHeaders(),
    withCredentials: true
  })
    .then(onLoginSuccess)
    .catch((error) => onLoginError(error, payload.messages));

export const doLoginAction = (
  payload: Payload,
  brand: string = TVGConf().brand
) => geoComplyMiddleware(payload, brand, loginRequest);
